/**
 * Demo Mode "Safe Redirect" lock.
 *
 * When a visitor arrives at `/business/demo?demo=1` (via the "Explore the
 * fan experience" link on the /football-challenge marketing page), we flip
 * `sessionStorage.demoLocked = "1"`. From that moment on:
 *   - Any attempt to navigate to a non-demo URL (typing `/admin`, `/login`,
 *     `/c/<slug>`, etc.) is intercepted and rewritten back to
 *     `/business/demo`.
 *   - The lock survives full-page reloads within the same tab.
 *   - The only ways out are (a) closing the tab or (b) clicking the
 *     "Exit Demo" button on the demo page — which clears the flag and
 *     routes the user back to `/football-challenge`.
 *
 * The lock is intentionally opt-in: `/business/demo` visited WITHOUT the
 * `?demo=1` param (e.g. admin/QA jumping straight to the sandbox) does
 * not activate the guard.
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const DEMO_LOCK_KEY = "demoLocked";
export const DEMO_PATH = "/business/demo";

export function isDemoLocked() {
  try {
    return sessionStorage.getItem(DEMO_LOCK_KEY) === "1";
  } catch {
    return false;
  }
}

export function activateDemoLock() {
  try {
    sessionStorage.setItem(DEMO_LOCK_KEY, "1");
  } catch {
    /* sessionStorage unavailable — silently no-op */
  }
}

export function clearDemoLock() {
  try {
    sessionStorage.removeItem(DEMO_LOCK_KEY);
  } catch {
    /* no-op */
  }
}

export default function DemoLockGuard() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Arrival via the marketing "Explore the fan experience" link:
    // ?demo=1 activates the lock ONCE, then we clean the query string.
    if (location.pathname === DEMO_PATH) {
      const params = new URLSearchParams(location.search);
      if (params.get("demo") === "1") {
        activateDemoLock();
        params.delete("demo");
        const nextSearch = params.toString();
        navigate(
          { pathname: DEMO_PATH, search: nextSearch ? `?${nextSearch}` : "" },
          { replace: true }
        );
        return;
      }
    }

    // If locked and the user has navigated off the demo, snap them back.
    if (isDemoLocked() && location.pathname !== DEMO_PATH) {
      navigate(DEMO_PATH, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  return null;
}
